import type { WorkflowRuleAction } from '@/pages/ats-job-creation/tabs/interview-plan/stage/automation/api/workflowRule.types';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

const STR = MOVE_WITH_AUTOMATION_STRINGS;

// BE stores delay in minutes despite the `delay_seconds` column name
// — see workflowRule.types.ts. Pick the largest natural unit so 1440
// renders as "1 day" and 60 as "1 hour"; anything else falls back to
// minutes so callers never see a fractional number.
const minutesToDurationLabel = (minutes: number): string => {
  if (minutes <= 0) return '';
  if (minutes % 1440 === 0) {
    return STR.duration.days(minutes / 1440);
  }
  if (minutes % 60 === 0) {
    return STR.duration.hours(minutes / 60);
  }
  return STR.duration.minutes(minutes);
};

type ActionConfig = {
  ats_job_stage_action_id?: number;
  // BE trigger vocabulary: `sent`, plus the submission family
  // (`candidate_submitted` / `scorecard_submitted`). `submitted` is the
  // legacy single value, kept so older rules still format correctly.
  trigger_on?:
    | 'sent'
    | 'scheduled'
    | 'completed'
    | 'submitted'
    | 'candidate_submitted'
    | 'scorecard_submitted';
};

// Any submission trigger collapses to the "After X is submitted" wording —
// candidate-side and scorecard-side both read the same here.
const SUBMITTED_TRIGGERS = new Set([
  'submitted',
  'candidate_submitted',
  'scorecard_submitted',
]);

const labelFor = (action: WorkflowRuleAction): string =>
  action.target?.label ?? action.target?.kind ?? STR.fallback.previousActivity;

// Returns a human-readable timing string for a single workflow rule
// action, given the full ordered actions list (so we can look up the
// previous action's label for chained triggers).
//
// Position 1 actions trigger off "stage entry". Position > 1 actions
// trigger relative to the action immediately before them in the rule.
export const formatAutomationTiming = (
  action: WorkflowRuleAction,
  actions: WorkflowRuleAction[]
): string => {
  const delayMinutes = action.delay_seconds ?? 0;
  const config = (action.action_config ?? {}) as ActionConfig;
  const isFirst = action.position === 1;

  const previous = actions.find((a) => a.position === action.position - 1);
  const previousLabel = previous
    ? labelFor(previous)
    : STR.fallback.previousActivity;

  if (isFirst) {
    if (delayMinutes > 0) {
      return STR.timing.afterStageEntry(minutesToDurationLabel(delayMinutes));
    }
    return STR.timing.immediately;
  }

  // Children may carry trigger_on: "submitted" (fire when previous
  // activity's scorecards/submission lands) or "sent" (fire when the
  // previous activity is sent / link delivered). When set, this wins
  // over delay — they're alternative trigger semantics, not additive.
  if (config.trigger_on && SUBMITTED_TRIGGERS.has(config.trigger_on)) {
    return STR.timing.afterSubmitted(previousLabel);
  }
  if (config.trigger_on === 'scheduled') {
    return STR.timing.afterScheduled(previousLabel);
  }
  if (config.trigger_on === 'completed') {
    return STR.timing.afterCompleted(previousLabel);
  }
  if (config.trigger_on === 'sent') {
    return STR.timing.afterSent(previousLabel);
  }

  if (delayMinutes > 0) {
    return STR.timing.afterPrevious(
      minutesToDurationLabel(delayMinutes),
      previousLabel
    );
  }
  return STR.timing.immediatelyAfter(previousLabel);
};
